import type { BeckyModel, ComponentsNativeMappers, NativeExtensionsTypes } from '@wix/thunderbolt-becky-root'
import _ from 'lodash'
import * as AddressInput from './components/AddressInput/AddressInput.mapper'
import * as AdminLoginButton from './components/AdminLoginButton/AdminLoginButton.mapper'
import * as AppPart from './components/AppPart/AppPart.mapper'
import * as AppPart2 from './components/AppPart/AppPart2.mapper'
import * as AppWidget from './components/AppWidget/AppWidget.mapper'
import * as BackgroundGroup from './components/BackgroundGroup/BackgroundGroup.mapper'
import * as BackToTopButton from './components/BackToTopButton/BackToTopButton.mapper'
import * as Checkbox from './components/Checkbox/Checkbox.mapper'
import * as CheckboxGroup from './components/CheckboxGroup/CheckboxGroup.mapper'
import * as ClassicSection from './components/ClassicSection/ClassicSection.mapper'
import { designCss as SectionDesignCss, css as SectionCss } from './components/Section/Section.mapper'
import * as ComboBoxInput from './components/ComboBoxInput/ComboBoxInput.mapper'
import * as ComboBoxInputListModal from './components/ComboBoxInput/ComboBoxInputListModal.mapper'
import * as Container from './components/Container/Container.mapper'
import * as DatePicker from './components/DatePicker/DatePicker.mapper'
import * as DropDownMenu from './components/DropDownMenu/DropDownMenu.mapper'
import * as ExpandableMenu from './components/ExpandableMenu/ExpandableMenu.mapper'
import * as FacebookLikeBox from './components/FacebookLikeBox/FacebookLikeBox.mapper'
import * as FileUploader from './components/FileUploader/FileUploader.mapper'
import * as FiveGridLine from './components/FiveGridLine/FiveGridLine.mapper'
import * as FooterContainer from './components/FooterContainer/FooterContainer.mapper'
import * as ItunesButton from './components/ItunesButton/ItunesButton.mapper'
import * as HeaderContainer from './components/HeaderContainer/HeaderContainer.mapper'
import { css as HeaderSectionCss } from './components/HeaderSection/HeaderSection.mapper'
import * as LanguageSelector from './components/LanguageSelector/LanguageSelector.mapper'
import { props as linkBarProps, css as linkBarCss } from './components/LinkBar/LinkBar.mapper'
import * as LoginSocialBar from './components/LoginSocialBar/LoginSocialBar.mapper'
import * as MediaContainer from './components/MediaContainer/MediaContainer.mapper'
import * as Menu from './components/Menu/Menu.mapper'
import * as MenuContainer from './components/MenuContainer/MenuContainer.mapper'
import * as MenuToggle from './components/MenuToggle/MenuToggle.mapper'
import * as Page from './components/Page/Page.mapper'
import * as PageGroup from './components/PageGroup/PageGroup.mapper'
import * as PaginatedGridGallery from './components/PaginatedGridGallery/PaginatedGridGallery.mapper'
import * as Pagination from './components/Pagination/Pagination.mapper'
import * as PopupCloseIconButton from './components/PopupCloseIconButton/PopupCloseIconButton.mapper'
import * as QuickActionBar from './components/QuickActionBar/QuickActionBar.mapper'
import * as RadioGroup from './components/RadioGroup/RadioGroup.mapper'
import * as RatingsDisplay from './components/RatingsDisplay/RatingsDisplay.mapper'
import * as RatingsInput from './components/RatingsInput/RatingsInput.mapper'
import * as RefComponent from './components/RefComponent/RefComponent.mapper'
import * as RichTextBox from './components/RichTextBox/RichTextBox.mapper'
import * as ScreenWidthContainer from './components/ScreenWidthContainer/ScreenWidthContainer.mapper'
import * as SearchBox from './components/SearchBox/SearchBox.mapper'
import * as SelectableContainer from './components/SelectableContainer/SelectableContainer.mapper'
import * as SelectionTagsList from './components/SelectionTagsList/SelectionTagsList.mapper'
import * as SignatureInput from './components/SignatureInput/SignatureInput.mapper'
import * as SiteButton from './components/SiteButton/SiteButton.mapper'
import * as SkypeCallButton from './components/SkypeCallButton/SkypeCallButton.mapper'
import * as SlideShowSlide from './components/SlideShowSlide/SlideShowSlide.mapper'
import * as StripColumnsContainer from './components/StripColumnsContainer/StripColumnsContainer.mapper'
import * as Column from './components/StripColumnsContainer/Column.mapper'
import * as TextAreaInput from './components/TextAreaInput/TextAreaInput.mapper'
import * as TextInput from './components/TextInput/TextInput.mapper'
import * as TimePicker from './components/TimePicker/TimePicker.mapper'
import * as TinyMenu from './components/TinyMenu/TinyMenu.mapper'
import * as VerticalMenu from './components/VerticalMenu/VerticalMenu.mapper'
import * as VectorImage from './components/VectorImage/VectorImage.mapper'
import * as WPhoto from './components/WPhoto/WPhoto.mapper'
import * as ImageX from './components/ImageX/ImageX.mapper'
import * as VideoBox from './components/VideoBox/VideoBox.mapper'
import * as WFacebookComment from './components/WFacebookComment/WFacebookComment.mapper'
import {
	selectorToCss as WRichTextSelectorToCss,
	css as WRichTextCss,
	props as WRichTextProps,
} from './components/WRichText/WRichText.mapper'
import * as Builder from './components/Builder/Builder.mapper'
import * as PopupContainer from './components/PopupContainer/PopupContainer.mapper'
import * as PopupPage from './components/PopupPage/PopupPage.mapper'
import * as PageBackground from './components/PageBackground/PageBackground.mapper'
import * as TPAWidget from './components/TPAWidget/TPAWidget.mapper'
import * as HoverBox from './components/HoverBox/HoverBox.mapper'
import * as VideoBoxAudio from './components/VideoBox/VideoBoxAudio.mapper'
import * as VideoBoxPlay from './components/VideoBox/VideoBoxPlay.mapper'
import * as Repeater from './components/Repeater/Repeater.mapper'
import * as HtmlComponent from './components/HtmlComponent/HtmlComponent.mapper'
import type { Logger } from '@wix/thunderbolt-becky-types'
import { NativeExtension } from '@wix/thunderbolt-components-native'

export type MergeMappers = (
	mappers: ComponentsNativeMappers,
	isExperimentOpen: (name: keyof BeckyModel['experiments']) => boolean,
	logger?: Logger
) => ComponentsNativeMappers

export const getCatharsisMappers = (isExperimentOpen: (name: keyof BeckyModel['experiments']) => boolean) => {
	const WRichText = {
		...{ css: WRichTextCss },
		selectorToCss: WRichTextSelectorToCss,
		...(isExperimentOpen('specs.thunderbolt.WRichTextPropsMapper') && { props: WRichTextProps }),
	}

	const Section = {
		css: SectionCss,
		designCss: SectionDesignCss,
	}

	const HeaderSection = {
		css: HeaderSectionCss,
		designCss: SectionDesignCss,
	}

	const isMigrateMenuPropsMapper =
		isExperimentOpen('specs.thunderbolt.MigrateMenuPropsMapper') &&
		isExperimentOpen('specs.thunderbolt.resolveLinksInPlace') &&
		isExperimentOpen('specs.thunderbolt.menuResolver')

	const LinkBar = {
		css: linkBarCss,
		...(isExperimentOpen('specs.thunderbolt.linkBarNativeMapper') &&
			isExperimentOpen('specs.thunderbolt.resolveLinksInPlace') && { props: linkBarProps }),
	}

	return {
		AddressInput,
		AdminLoginButton,
		AppPart,
		AppPart2,
		AppWidget,
		BackToTopButton,
		BackgroundGroup,
		Checkbox,
		CheckboxGroup,
		ComboBoxInput,
		ComboBoxInputListModal,
		Container,
		DatePicker,
		DropDownMenu,
		ExpandableMenu,
		FacebookLikeBox,
		FileUploader,
		FiveGridLine,
		FooterContainer,
		FooterSection: Section,
		HeaderContainer,
		HeaderSection,
		ItunesButton,
		LanguageSelector,
		LinkBar,
		LoginSocialBar,
		MediaContainer,
		MenuContainer,
		MenuToggle,
		Page,
		PageGroup,
		PaginatedGridGallery,
		Pagination,
		PopupCloseIconButton,
		PopupContainer,
		PopupPage,
		QuickActionBar,
		RadioGroup,
		RatingsDisplay,
		RatingsInput,
		RichTextBox,
		ScreenWidthContainer,
		SearchBox,
		...(!_.isEmpty(Section) && { Section }),
		SelectionTagsList,
		SignatureInput,
		SiteButton,
		SkypeCallButton,
		SlideShowSlide,
		TextAreaInput,
		TextInput,
		TimePicker,
		TinyMenu,
		VerticalMenu,
		WFacebookComment,
		Builder,
		...(!_.isEmpty(WRichText) && { WRichText }),
		PageBackground,
		StripColumnsContainer,
		ClassicSection,
		Column,
		RefComponent,
		SelectableContainer,
		Repeater,
		HoverBox,
		VectorImage,
		WPhoto,
		ImageX,
		MediaControls: VideoBoxAudio,
		MediaOverlayControls: VideoBoxPlay,
		MediaPlayer: VideoBox,
		TPAWidget,
		TPAGluedWidget: TPAWidget,
		TPASection: TPAWidget,
		TPAMultiSection: TPAWidget,
		...(isExperimentOpen('specs.thunderbolt.HtmlComponentPropsMapper') && { HtmlComponent }),
		...(isMigrateMenuPropsMapper && { Menu }),
	}
}

const wrapWithReportError = <Args extends Array<any>, Res>(
	resolver: (...args: Args) => Res,
	componentType: string,
	mapperType: NativeExtensionsTypes,
	logger?: Logger
) => {
	return (...args: Args) => {
		try {
			return resolver(...args)
		} catch (error) {
			const msg = `Failed executing ${mapperType} mapper for componentType ${componentType}: ${error?.message}`
			console.error(msg, error)
			logger?.warn(msg, error)
			return mapperType in ['css', 'designCss', 'selectorToCss', 'uiType'] ? '' : {}
		}
	}
}

export const mergeMappers: MergeMappers = (mappers, isExperimentOpen, logger?: Logger) => {
	const catharsisMappers = _.omit(getCatharsisMappers(isExperimentOpen))
	const mergedMappers: ComponentsNativeMappers = _.assignWith(
		{},
		mappers,
		catharsisMappers,
		(external: ComponentsNativeMappers[string], internal: ComponentsNativeMappers[string]) => {
			// If only EE or internal component definition (e.g. all mappers), return it
			if (!external || !internal) {
				return external || internal
			}

			return _.assignWith({}, external, internal, (ext, int) => {
				// If only EE or internal mapper exists, return it
				if (!ext || !int) {
					return ext || int
				}

				// If both internal and EE mapper exist, merge them
				const mapper: NativeExtension = {
					deps: { ...int.deps, ...ext.deps },
					resolver: (deps, carmiProps) => {
						// Calculate CARMI data using internal mapper, then pass it to the EE mapper
						const carmiData = int.resolver(deps, carmiProps)
						return ext.resolver(deps, carmiData)
					},
				}

				return mapper
			})
		}
	)

	return _.transform(
		mergedMappers,
		(result, specificComponentMappers, componentType) => {
			result[componentType] = _.mapValues<ComponentsNativeMappers[string]>(
				specificComponentMappers,
				(mapper: NativeExtension, mapperType: NativeExtensionsTypes) => ({
					deps: mapper.deps,
					resolver: wrapWithReportError(mapper.resolver, componentType, mapperType, logger),
				})
			)
		},
		{} as ComponentsNativeMappers
	)
}
